/**
 * The live camera the shell runs for an app (needs the app's "camera" permission).
 * Apps are sandboxed with an opaque origin, which browsers (and Android's WebView)
 * never give the camera to, so the shell opens it and draws the preview over the
 * part of the app that asked for it. The app gets photos, never the stream.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { CameraOpenParams, CameraRect } from "@omni/sdk/protocol";

import { BridgeError } from "./bridge";

export interface CameraView {
  rect: CameraRect;
  guide: CameraOpenParams["guide"];
  radius: number;
  flash: boolean;
}

export function useAppCamera() {
  const [view, setView] = useState<CameraView | null>(null);
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const facing = useRef<CameraOpenParams["facing"]>("environment");

  const start = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) throw new BridgeError("There's no camera on this device", 404);
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facing.current ?? "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      stream.current?.getTracks().forEach((t) => t.stop());
      stream.current = s;
      if (video.current) {
        video.current.srcObject = s;
        void video.current.play().catch(() => undefined);
      }
      return s;
    } catch (e) {
      const name = (e as DOMException).name;
      if (name === "NotAllowedError" || name === "SecurityError") throw new BridgeError("Camera access was refused", 401);
      throw new BridgeError("The camera isn't available", 404);
    }
  }, []);

  const stop = useCallback(() => {
    stream.current?.getTracks().forEach((t) => t.stop()); // the camera light goes off
    stream.current = null;
  }, []);

  const close = useCallback(() => {
    stop();
    setView(null);
  }, [stop]);

  // The <video> mounts after the view appears: attach the running stream to it
  useEffect(() => {
    if (view && video.current && stream.current && video.current.srcObject !== stream.current) {
      video.current.srcObject = stream.current;
      void video.current.play().catch(() => undefined);
    }
  }, [view]);

  // Off in the background, back on when Omni returns
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") stop();
      else if (view && !stream.current) void start().catch(() => close());
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [view, start, stop, close]);

  useEffect(() => close, [close]);

  const api = {
    open: async (p: CameraOpenParams) => {
      facing.current = p.facing ?? "environment";
      const s = await start();
      setView({ rect: p.rect, guide: p.guide ?? null, radius: p.radius ?? 16, flash: false });
      const settings = s.getVideoTracks()[0]?.getSettings() ?? {};
      return { width: settings.width ?? 0, height: settings.height ?? 0 };
    },
    move: (p: { rect?: CameraRect; guide?: CameraOpenParams["guide"] }) =>
      setView((v) => v && { ...v, ...(p.rect ? { rect: p.rect } : {}), ...(p.guide !== undefined ? { guide: p.guide } : {}) }),
    capture: async (): Promise<Blob> => {
      const v = video.current;
      if (!v || !v.videoWidth) throw new BridgeError("The camera isn't ready yet", 409);
      const canvas = document.createElement("canvas");
      canvas.width = v.videoWidth;
      canvas.height = v.videoHeight;
      canvas.getContext("2d")!.drawImage(v, 0, 0);
      setView((cur) => cur && { ...cur, flash: true });
      setTimeout(() => setView((cur) => cur && { ...cur, flash: false }), 160);
      const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", 0.9));
      if (!blob) throw new BridgeError("Couldn't take the photo", 500);
      return blob;
    },
    close,
  };

  return { view, video, api, close };
}
